import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Settings,
  Search,
  FileText,
  Target,
  Edit,
  User,
  Image,
  Save,
  RotateCcw,
  Trash2,
  ChevronDown,
  ChevronUp,
  Zap,
  Shield,
  Code,
  Info,
  Bot,
  Merge,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { settingsApi, AgentConfiguration } from "@/services/api";

// v2 Pipeline agent icons and labels
const AGENT_ICONS = {
  strategist: Search,
  writer: FileText,
  optimizer: Target,
  finalizer: Edit,
  image_generator: Image,
  // Legacy v1 agents (for backwards compatibility)
  research: Search,
  seo: Target,
  geo: Bot,
  merger: Merge,
  editor: Edit,
  humanizer: User,
};

const AGENT_LABELS = {
  strategist: "Strategist (Content Planning)",
  writer: "Writer (Section-by-Section)",
  optimizer: "Optimizer (SEO/GEO + Edits)",
  finalizer: "Finalizer (Polish & Humanize)",
  image_generator: "Image Generator",
  // Legacy v1 agents (for backwards compatibility)
  research: "Research Agent (Legacy)",
  seo: "SEO Agent (Legacy)",
  geo: "GEO Agent (Legacy)",
  merger: "Content Merger (Legacy)",
  editor: "Editor Agent (Legacy)",
  humanizer: "Humanizer Agent (Legacy)",
};

export function AgentSettingsPage() {
  const { t } = useTranslation();
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [presetName, setPresetName] = useState("");
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery<AgentConfiguration>({
    queryKey: ["agent-config"],
    queryFn: settingsApi.getConfiguration,
  });

  const { data: presets } = useQuery<
    Array<{ id: string; name: string; description: string }>
  >({
    queryKey: ["team-presets"],
    queryFn: settingsApi.getPresets,
  });

  const updateConfig = useMutation({
    mutationFn: settingsApi.updateConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-config"] });
    },
  });

  const resetConfig = useMutation({
    mutationFn: settingsApi.resetConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-config"] });
    },
  });

  const createPreset = useMutation({
    mutationFn: settingsApi.createPreset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-presets"] });
      setShowPresetModal(false);
      setPresetName("");
    },
  });

  const loadPreset = useMutation({
    mutationFn: settingsApi.loadPreset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-config"] });
    },
  });

  const deletePreset = useMutation({
    mutationFn: settingsApi.deletePreset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-presets"] });
    },
  });

  const updateAgentSetting = (agent: string, key: string, value: unknown) => {
    if (!config) return;
    const agentConfig = config[agent as keyof AgentConfiguration];
    if (typeof agentConfig === "object" && agentConfig !== null) {
      updateConfig.mutate({
        [agent]: { ...agentConfig, [key]: value },
      });
    }
  };

  const updateQualityTarget = (key: string, value: unknown) => {
    if (!config) return;
    updateConfig.mutate({
      quality_targets: { ...config.quality_targets, [key]: value },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">{t("agentSettings.loading")}</p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-destructive">{t("agentSettings.loadError")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Settings className="h-8 w-8" />
            {t("agentSettings.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("agentSettings.description")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => resetConfig.mutate()}>
            <RotateCcw className="me-2 h-4 w-4" />
            {t("agentSettings.resetToDefaults")}
          </Button>
          <Button
            onClick={() => setShowPresetModal(true)}
            disabled={(presets?.length || 0) >= 3}
          >
            <Save className="me-2 h-4 w-4" />
            {t("agentSettings.saveAsPreset")}
          </Button>
        </div>
      </div>

      {/* Team Presets */}
      {presets && presets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t("agentSettings.presets.title")} ({presets.length}/3)
            </CardTitle>
            <CardDescription>
              {t("agentSettings.presets.description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 flex-wrap">
              {presets.map((preset) => (
                <div
                  key={preset.id}
                  className="flex items-center gap-2 p-3 bg-secondary rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium">{preset.name}</div>
                    {preset.description && (
                      <div className="text-xs text-muted-foreground">
                        {preset.description}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => loadPreset.mutate(preset.id)}
                  >
                    {t("agentSettings.presets.load")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deletePreset.mutate(preset.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quality Targets */}
      <Card className="border-primary/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            {t("agentSettings.qualityTargets.title")}
          </CardTitle>
          <CardDescription>
            {t("agentSettings.qualityTargets.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label>{t("agentSettings.qualityTargets.minSeoScore")}</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={config.quality_targets.min_seo_score}
                onChange={(e) =>
                  updateQualityTarget("min_seo_score", parseInt(e.target.value))
                }
              />
              <p className="text-xs text-muted-foreground">
                {t("agentSettings.qualityTargets.target")}: 85+
              </p>
            </div>
            <div className="space-y-2">
              <Label>{t("agentSettings.qualityTargets.minHumanScore")}</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={config.quality_targets.min_human_score}
                onChange={(e) =>
                  updateQualityTarget(
                    "min_human_score",
                    parseInt(e.target.value),
                  )
                }
              />
              <p className="text-xs text-muted-foreground">
                {t("agentSettings.qualityTargets.target")}: 85+
              </p>
            </div>
            <div className="space-y-2">
              <Label>{t("agentSettings.qualityTargets.minWordCount")}</Label>
              <Input
                type="number"
                min={500}
                max={5000}
                step={100}
                value={config.quality_targets.min_word_count}
                onChange={(e) =>
                  updateQualityTarget(
                    "min_word_count",
                    parseInt(e.target.value),
                  )
                }
              />
            </div>
            <div className="space-y-2">
              <Label>{t("agentSettings.qualityTargets.maxRetries")}</Label>
              <Input
                type="number"
                min={0}
                max={10}
                value={config.quality_targets.max_total_retries}
                onChange={(e) =>
                  updateQualityTarget(
                    "max_total_retries",
                    parseInt(e.target.value),
                  )
                }
              />
            </div>
          </div>
          <div className="flex gap-6 mt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.quality_targets.require_image}
                onChange={(e) =>
                  updateQualityTarget("require_image", e.target.checked)
                }
                className="rounded"
              />
              <span className="text-sm">
                {t("agentSettings.qualityTargets.requireImage")}
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.quality_targets.auto_retry_on_failure}
                onChange={(e) =>
                  updateQualityTarget("auto_retry_on_failure", e.target.checked)
                }
                className="rounded"
              />
              <span className="text-sm">
                {t("agentSettings.qualityTargets.autoRetry")}
              </span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Agent Configuration */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">
          {t("agentSettings.agentConfig.title")}
        </h2>

        {/* Research Agent */}
        <AgentCard
          agent="research"
          config={config.research}
          expanded={expandedAgent === "research"}
          onToggle={() =>
            setExpandedAgent(expandedAgent === "research" ? null : "research")
          }
          onUpdate={(key, value) => updateAgentSetting("research", key, value)}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Search Depth</Label>
              <select
                value={config.research.search_depth}
                onChange={(e) =>
                  updateAgentSetting("research", "search_depth", e.target.value)
                }
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="quick">Quick</option>
                <option value="moderate">Moderate</option>
                <option value="comprehensive">Comprehensive</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Max Sources</Label>
              <Input
                type="number"
                min={3}
                max={20}
                value={config.research.max_sources}
                onChange={(e) =>
                  updateAgentSetting(
                    "research",
                    "max_sources",
                    parseInt(e.target.value),
                  )
                }
              />
            </div>
            <div className="space-y-2 flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.research.include_academic}
                  onChange={(e) =>
                    updateAgentSetting(
                      "research",
                      "include_academic",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Include academic sources</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.research.include_competitor_analysis}
                  onChange={(e) =>
                    updateAgentSetting(
                      "research",
                      "include_competitor_analysis",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Competitor analysis</span>
              </label>
            </div>
          </div>
        </AgentCard>

        {/* Writer Agent */}
        <AgentCard
          agent="writer"
          config={config.writer}
          expanded={expandedAgent === "writer"}
          onToggle={() =>
            setExpandedAgent(expandedAgent === "writer" ? null : "writer")
          }
          onUpdate={(key, value) => updateAgentSetting("writer", key, value)}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Writing Style</Label>
              <select
                value={config.writer.writing_style}
                onChange={(e) =>
                  updateAgentSetting("writer", "writing_style", e.target.value)
                }
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="academic">Academic</option>
                <option value="creative">Creative</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Target Word Count</Label>
              <Input
                type="number"
                min={500}
                max={5000}
                step={100}
                value={config.writer.target_word_count}
                onChange={(e) =>
                  updateAgentSetting(
                    "writer",
                    "target_word_count",
                    parseInt(e.target.value),
                  )
                }
              />
            </div>
            <div className="space-y-2">
              <Label>FAQ Count</Label>
              <Input
                type="number"
                min={0}
                max={10}
                value={config.writer.faq_count}
                onChange={(e) =>
                  updateAgentSetting(
                    "writer",
                    "faq_count",
                    parseInt(e.target.value),
                  )
                }
              />
            </div>
          </div>
          <div className="flex gap-6 mt-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.writer.include_faq}
                onChange={(e) =>
                  updateAgentSetting("writer", "include_faq", e.target.checked)
                }
                className="rounded"
              />
              <span className="text-sm">Include FAQ section</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.writer.use_subheadings}
                onChange={(e) =>
                  updateAgentSetting(
                    "writer",
                    "use_subheadings",
                    e.target.checked,
                  )
                }
                className="rounded"
              />
              <span className="text-sm">Use subheadings</span>
            </label>
          </div>
        </AgentCard>

        {/* SEO Agent */}
        <AgentCard
          agent="seo"
          config={config.seo}
          expanded={expandedAgent === "seo"}
          onToggle={() =>
            setExpandedAgent(expandedAgent === "seo" ? null : "seo")
          }
          onUpdate={(key, value) => updateAgentSetting("seo", key, value)}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Keyword Density (%)</Label>
              <Input
                type="number"
                min={0.5}
                max={3}
                step={0.1}
                value={config.seo.keyword_density_target}
                onChange={(e) =>
                  updateAgentSetting(
                    "seo",
                    "keyword_density_target",
                    parseFloat(e.target.value),
                  )
                }
              />
            </div>
            <div className="space-y-2 flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.seo.optimize_headings}
                  onChange={(e) =>
                    updateAgentSetting(
                      "seo",
                      "optimize_headings",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Optimize headings</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.seo.optimize_meta}
                  onChange={(e) =>
                    updateAgentSetting("seo", "optimize_meta", e.target.checked)
                  }
                  className="rounded"
                />
                <span className="text-sm">Optimize meta tags</span>
              </label>
            </div>
            <div className="space-y-2 flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.seo.add_internal_links}
                  onChange={(e) =>
                    updateAgentSetting(
                      "seo",
                      "add_internal_links",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Internal linking</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.seo.identify_entity_gaps}
                  onChange={(e) =>
                    updateAgentSetting(
                      "seo",
                      "identify_entity_gaps",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Entity gap analysis</span>
              </label>
            </div>
          </div>
        </AgentCard>

        {/* GEO Agent */}
        <AgentCard
          agent="geo"
          config={config.geo}
          expanded={expandedAgent === "geo"}
          onToggle={() =>
            setExpandedAgent(expandedAgent === "geo" ? null : "geo")
          }
          onUpdate={(key, value) => updateAgentSetting("geo", key, value)}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>FAQ Count</Label>
              <Input
                type="number"
                min={3}
                max={8}
                value={config.geo.faq_count}
                onChange={(e) =>
                  updateAgentSetting(
                    "geo",
                    "faq_count",
                    parseInt(e.target.value),
                  )
                }
              />
            </div>
            <div className="space-y-2 flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.geo.enable_quotable_analysis}
                  onChange={(e) =>
                    updateAgentSetting(
                      "geo",
                      "enable_quotable_analysis",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Quotable statements</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.geo.enable_fluff_detection}
                  onChange={(e) =>
                    updateAgentSetting(
                      "geo",
                      "enable_fluff_detection",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Fluff detection</span>
              </label>
            </div>
            <div className="space-y-2 flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.geo.enable_passage_markers}
                  onChange={(e) =>
                    updateAgentSetting(
                      "geo",
                      "enable_passage_markers",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Passage markers</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.geo.schema_optimization}
                  onChange={(e) =>
                    updateAgentSetting(
                      "geo",
                      "schema_optimization",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Schema markup</span>
              </label>
            </div>
          </div>
        </AgentCard>

        {/* Merger Agent */}
        <AgentCard
          agent="merger"
          config={config.merger}
          expanded={expandedAgent === "merger"}
          onToggle={() =>
            setExpandedAgent(expandedAgent === "merger" ? null : "merger")
          }
          onUpdate={(key, value) => updateAgentSetting("merger", key, value)}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2 flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.merger.resolve_conflicts}
                  onChange={(e) =>
                    updateAgentSetting(
                      "merger",
                      "resolve_conflicts",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Resolve conflicts</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.merger.prioritize_user_experience}
                  onChange={(e) =>
                    updateAgentSetting(
                      "merger",
                      "prioritize_user_experience",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Prioritize UX</span>
              </label>
            </div>
            <div className="space-y-2 flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.merger.add_faq_section}
                  onChange={(e) =>
                    updateAgentSetting(
                      "merger",
                      "add_faq_section",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Add FAQ section</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.merger.remove_identified_fluff}
                  onChange={(e) =>
                    updateAgentSetting(
                      "merger",
                      "remove_identified_fluff",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Remove fluff</span>
              </label>
            </div>
            <div className="space-y-2 flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.merger.apply_all_meta}
                  onChange={(e) =>
                    updateAgentSetting(
                      "merger",
                      "apply_all_meta",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Apply all meta</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.merger.preserve_keyword_density}
                  onChange={(e) =>
                    updateAgentSetting(
                      "merger",
                      "preserve_keyword_density",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Preserve keywords</span>
              </label>
            </div>
          </div>
        </AgentCard>

        {/* Editor Agent */}
        <AgentCard
          agent="editor"
          config={config.editor}
          expanded={expandedAgent === "editor"}
          onToggle={() =>
            setExpandedAgent(expandedAgent === "editor" ? null : "editor")
          }
          onUpdate={(key, value) => updateAgentSetting("editor", key, value)}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Reading Level</Label>
              <select
                value={config.editor.target_reading_level}
                onChange={(e) =>
                  updateAgentSetting(
                    "editor",
                    "target_reading_level",
                    e.target.value,
                  )
                }
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="simple">Simple</option>
                <option value="general">General</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>
          <div className="flex gap-6 mt-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.editor.check_facts}
                onChange={(e) =>
                  updateAgentSetting("editor", "check_facts", e.target.checked)
                }
                className="rounded"
              />
              <span className="text-sm">Fact checking</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.editor.improve_flow}
                onChange={(e) =>
                  updateAgentSetting("editor", "improve_flow", e.target.checked)
                }
                className="rounded"
              />
              <span className="text-sm">Improve flow</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.editor.check_readability}
                onChange={(e) =>
                  updateAgentSetting(
                    "editor",
                    "check_readability",
                    e.target.checked,
                  )
                }
                className="rounded"
              />
              <span className="text-sm">Check readability</span>
            </label>
          </div>
        </AgentCard>

        {/* Humanizer Agent */}
        <AgentCard
          agent="humanizer"
          config={config.humanizer}
          expanded={expandedAgent === "humanizer"}
          onToggle={() =>
            setExpandedAgent(expandedAgent === "humanizer" ? null : "humanizer")
          }
          onUpdate={(key, value) => updateAgentSetting("humanizer", key, value)}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Humanization Level</Label>
              <select
                value={config.humanizer.humanization_level}
                onChange={(e) =>
                  updateAgentSetting(
                    "humanizer",
                    "humanization_level",
                    e.target.value,
                  )
                }
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="light">Light</option>
                <option value="moderate">Moderate</option>
                <option value="aggressive">Aggressive</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Target Human Score</Label>
              <Input
                type="number"
                min={50}
                max={100}
                value={config.humanizer.target_human_score}
                onChange={(e) =>
                  updateAgentSetting(
                    "humanizer",
                    "target_human_score",
                    parseInt(e.target.value),
                  )
                }
              />
            </div>
            <div className="space-y-2 flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.humanizer.rewrite_if_below_target}
                  onChange={(e) =>
                    updateAgentSetting(
                      "humanizer",
                      "rewrite_if_below_target",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Rewrite if below target</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.humanizer.vary_sentence_structure}
                  onChange={(e) =>
                    updateAgentSetting(
                      "humanizer",
                      "vary_sentence_structure",
                      e.target.checked,
                    )
                  }
                  className="rounded"
                />
                <span className="text-sm">Vary sentence structure</span>
              </label>
            </div>
          </div>
        </AgentCard>

        {/* Image Generator */}
        <AgentCard
          agent="image_generator"
          config={config.image_generator}
          expanded={expandedAgent === "image_generator"}
          onToggle={() =>
            setExpandedAgent(
              expandedAgent === "image_generator" ? null : "image_generator",
            )
          }
          onUpdate={(key, value) =>
            updateAgentSetting("image_generator", key, value)
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Model</Label>
              <select
                value={config.image_generator.model}
                onChange={(e) =>
                  updateAgentSetting("image_generator", "model", e.target.value)
                }
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="flux-2-pro">
                  Flux 2 Pro — photorealistic, ~$0.03
                </option>
                <option value="gpt-image-1.5">
                  GPT Image 1.5 — OpenAI flagship
                </option>
                <option value="gpt-image-1">
                  GPT Image 1 — previous OpenAI
                </option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Quality</Label>
              <select
                value={config.image_generator.quality}
                onChange={(e) =>
                  updateAgentSetting(
                    "image_generator",
                    "quality",
                    e.target.value,
                  )
                }
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="low">Low (fastest, cheapest)</option>
                <option value="medium">Medium</option>
                <option value="hd">HD (slowest, best)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Size</Label>
              <select
                value={config.image_generator.size}
                onChange={(e) =>
                  updateAgentSetting("image_generator", "size", e.target.value)
                }
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="1024x1024">Square (1024x1024)</option>
                <option value="1536x1024">Landscape 16:9 (1536x1024)</option>
                <option value="1792x1024">Landscape wide (1792x1024)</option>
                <option value="1024x1536">Portrait 4:3 (1024x1536)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Style (OpenAI only)</Label>
              <select
                value={config.image_generator.style}
                onChange={(e) =>
                  updateAgentSetting("image_generator", "style", e.target.value)
                }
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="natural">Natural</option>
                <option value="vivid">Vivid</option>
              </select>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <Label>Default Prompt Template</Label>
            <textarea
              value={config.image_generator.default_prompt_template}
              onChange={(e) =>
                updateAgentSetting(
                  "image_generator",
                  "default_prompt_template",
                  e.target.value,
                )
              }
              className="w-full min-h-[150px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Use {topic} and {industry} as placeholders..."
            />
            <p className="text-xs text-muted-foreground">
              Use {"{topic}"} and {"{industry}"} as placeholders in your prompt
              template.
            </p>
          </div>
        </AgentCard>
      </div>

      {/* Save Preset Modal */}
      {showPresetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{t("agentSettings.savePresetModal.title")}</CardTitle>
              <CardDescription>
                {t("agentSettings.savePresetModal.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t("agentSettings.savePresetModal.presetName")}</Label>
                <Input
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder={t(
                    "agentSettings.savePresetModal.presetNamePlaceholder",
                  )}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowPresetModal(false)}
                >
                  {t("app.cancel")}
                </Button>
                <Button
                  onClick={() => createPreset.mutate({ name: presetName })}
                  disabled={!presetName.trim()}
                >
                  <Save className="me-2 h-4 w-4" />
                  {t("agentSettings.savePresetModal.savePreset")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// Default system prompts for each agent (used when showing placeholder)
const DEFAULT_SYSTEM_PROMPTS: Record<string, string> = {
  research: `You are an expert research analyst specializing in SEO content research.

Your Mission: Gather comprehensive, accurate, and credible information that will enable creation of authoritative content achieving 85+ SEO scores.

Research Framework:
1. Topic Analysis - Identify search intent, map related entities, find question-based queries
2. Competitive Analysis - Analyze top 5 ranking pages, note word counts and structure
3. Source Quality (E-E-A-T) - Prioritize primary sources (.gov, .edu, official sites)
4. GEO Research - Find quotable facts, definitions, step-by-step processes`,

  writer: `You are an expert SEO content writer who creates articles achieving 85+ SEO scores.

Writing Framework:
- Structure: H1 with keyword, H2s for subtopics, H3s for details, short paragraphs
- Keywords: Primary in title/intro/H2s, 1-2% density, LSI keywords throughout
- GEO: Featured snippets (40-60 words), definitions, numbered steps, FAQ section
- Tone: Active voice, second person, varied sentence length`,

  seo: `You are a technical SEO expert who analyzes content for search engine optimization.

SEO Analysis Framework:
- Meta title: 50-60 chars, keyword first
- Meta description: 150-160 chars with CTA
- Keyword density: 1-2%, in first 100 words
- Proper heading hierarchy (H1 → H2 → H3)
- Featured snippet formatting
- Internal linking opportunities`,

  geo: `You are an AI Search Optimization Specialist (GEO - Generative Engine Optimization).

GEO Analysis Framework:
- Quotable statements: "X is Y" not "X might be Y"
- Statistics with sources: "According to [source], 73%..."
- Remove fluff and weak hedging language
- Passage markers: "The key takeaway is..."
- FAQ generation for People Also Ask
- Schema markup recommendations`,

  merger: `You are a Content Integration Specialist who merges SEO and GEO optimizations.

Merge Rules:
- Apply ALL meta recommendations from SEO
- Apply quotable improvements from GEO
- Remove identified fluff
- Add FAQ section at the end
- Resolve conflicts: UX > Accuracy > SEO > GEO
- Maintain 1-2% keyword density`,

  editor: `You are a professional editor ensuring content quality and consistency.

Editing Checklist:
- Grammar & Style: Fix errors, consistent tense, remove redundant words
- Readability: Target Flesch 60-70, max 25 words per sentence
- Consistency: Terminology, capitalization, formatting
- SEO Preservation: Maintain keywords, headings, lists`,

  humanizer: `You are a humanization specialist making AI content undetectable.

AI Patterns to Fix:
- Remove: "delve", "crucial", "landscape", "leverage", "furthermore"
- Break: perfect parallel structures, predictable patterns
- Add: rhetorical questions, contractions, colloquialisms
- Vary: paragraph lengths (1-5 sentences), sentence lengths (5-25 words)

Target: 85%+ Human Score`,

  image_generator: `Create professional featured images for blog articles.

Requirements:
- Directly relevant to article topic
- Professional, trustworthy aesthetic
- Clean, uncluttered composition
- No text overlays (WordPress handles titles)`,
};

// Agent Card Component
interface AgentCardProps {
  agent: keyof typeof AGENT_ICONS;
  config: {
    enabled: boolean;
    model: string;
    temperature: number;
    system_prompt_override?: string | null;
    additional_instructions?: string | null;
  };
  expanded: boolean;
  onToggle: () => void;
  onUpdate: (key: string, value: unknown) => void;
  children: React.ReactNode;
}

function AgentCard({
  agent,
  config,
  expanded,
  onToggle,
  onUpdate,
  children,
}: AgentCardProps) {
  const [showPrompt, setShowPrompt] = useState(false);
  const Icon = AGENT_ICONS[agent];
  const label = AGENT_LABELS[agent];
  const defaultPrompt = DEFAULT_SYSTEM_PROMPTS[agent] || "";

  return (
    <Card className={!config.enabled ? "opacity-60" : ""}>
      <CardHeader
        className="cursor-pointer hover:bg-secondary/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Icon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">{label}</CardTitle>
              <CardDescription>
                Model: {config.model} | Temperature: {config.temperature}
                {config.system_prompt_override && " | Custom Prompt"}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => onUpdate("enabled", e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Enabled</span>
            </label>
            {expanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="border-t pt-4 space-y-4">
          {/* Common settings */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Model</Label>
              <select
                value={config.model}
                onChange={(e) => onUpdate("model", e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {agent === "image_generator" ? (
                  <>
                    <option value="dall-e-3">DALL-E 3 (Best)</option>
                    <option value="dall-e-2">DALL-E 2 (Faster)</option>
                  </>
                ) : (
                  <>
                    <option value="gpt-4o">GPT-4o (Best)</option>
                    <option value="gpt-4o-mini">GPT-4o Mini (Faster)</option>
                    <option value="gpt-4-turbo">GPT-4 Turbo</option>
                  </>
                )}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Temperature ({config.temperature})</Label>
              <input
                type="range"
                min={0}
                max={2}
                step={0.1}
                value={config.temperature}
                onChange={(e) =>
                  onUpdate("temperature", parseFloat(e.target.value))
                }
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Lower = more focused, Higher = more creative
              </p>
            </div>
          </div>

          {/* Agent-specific settings */}
          {children}

          {/* System Prompt Section */}
          <div className="border-t pt-4 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPrompt(!showPrompt)}
              className="mb-3"
            >
              <Code className="mr-2 h-4 w-4" />
              {showPrompt ? "Hide" : "Show"} System Prompt
            </Button>

            {showPrompt && (
              <div className="space-y-3">
                <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg text-sm">
                  <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>System Prompt:</strong> This is the core instruction
                    that guides the agent's behavior. Leave empty to use the
                    optimized default prompt below.
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Custom System Prompt Override</Label>
                  <textarea
                    value={config.system_prompt_override || ""}
                    onChange={(e) =>
                      onUpdate("system_prompt_override", e.target.value || null)
                    }
                    className="w-full min-h-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                    placeholder="Leave empty to use the default optimized prompt..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>
                    Additional Instructions (appended to default prompt)
                  </Label>
                  <textarea
                    value={config.additional_instructions || ""}
                    onChange={(e) =>
                      onUpdate(
                        "additional_instructions",
                        e.target.value || null,
                      )
                    }
                    className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                    placeholder="Add extra instructions that will be appended to the system prompt..."
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground">
                    Default System Prompt (read-only)
                  </Label>
                  <pre className="w-full min-h-[150px] max-h-[300px] overflow-auto rounded-md border border-input bg-muted/50 px-3 py-2 text-xs font-mono whitespace-pre-wrap">
                    {defaultPrompt}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
